/**
 * Compose a prospect briefing, and read it before it goes anywhere.
 *
 * The control for `POST /api/mo/briefing/edition`. It composes from posts he has already
 * published and nothing else; the server module carries the reasoning for why that is the whole
 * safety argument.
 *
 * NOTHING HERE SENDS, and the copy says so twice: on the button's own caption and in the caveats
 * the server returns with every edition. MO OS holds no mailing list, and he picks each recipient
 * in his own mail client, which is a judgement about a relationship rather than a query over a
 * table. That is also what keeps the distribution lawful.
 *
 * A REFUSAL IS SHOWN IN PLACE, not in the panel. "Only two posts went out this month" is one
 * sentence and opening a modal to deliver it would be theatre. An edition is eight paragraphs he
 * has to read properly, so that gets the panel.
 */

import { useState, type JSX } from 'react';
import { DeckDetail, type DeckDetailItem } from './DeckDetail.js';

interface Edition {
  subject: string;
  body: string;
  restsOn: string[];
  caveats: string[];
  at: string;
}

type Outcome = { ok: true; edition: Edition } | { ok: false; why: string };

/** The window to compose over. A month is roughly his publishing cadence times four. */
const DAYS = 30;

export function BriefingButton(): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState<string | null>(null);
  const [edition, setEdition] = useState<Edition | null>(null);

  const compose = (): void => {
    setBusy(true);
    setWhy(null);
    void (async () => {
      try {
        const res = await fetch('/api/mo/briefing/edition', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ days: DAYS }),
        });

        if (!res.ok) {
          // 501 names the missing variable, which is a real answer rather than a failure.
          let detail = `Composing failed (${res.status})`;
          try {
            const body = (await res.json()) as { error?: string };
            if (typeof body.error === 'string' && body.error !== '') detail = body.error;
          } catch {
            // Non-JSON body; the status is all there is.
          }
          setWhy(detail);
          return;
        }

        const out = (await res.json()) as Outcome;
        if (out.ok) setEdition(out.edition);
        else setWhy(out.why);
      } catch (err) {
        setWhy(`Composing failed: ${(err as Error).message}`);
      } finally {
        setBusy(false);
      }
    })();
  };

  const item = (e: Edition): DeckDetailItem => ({
    kind: 'Briefing',
    // It needs his judgement before it goes anywhere, which is what this tone means here.
    tone: 'judge',
    title: e.subject,
    meta: `Composed from ${e.restsOn.length} post${e.restsOn.length === 1 ? '' : 's'} you published`,
    provenance: 'draft, not sent',
    facts: [
      ...e.caveats.map((c, i) => ({ label: i === 0 ? 'Before you send' : ' ', value: c })),
      {
        label: 'Rests on',
        value: e.restsOn.map((p) => p.replace(/^.*\//, '').replace(/\.md$/, '')).join(', '),
      },
      { label: 'Composed', value: e.at.slice(0, 16).replace('T', ' ') },
    ],
    body: `Subject: ${e.subject}\n\n${e.body}`,
    actions: [
      {
        label: 'Copy the briefing',
        icon: 'action',
        hint: 'Then choose who gets it',
        keepOpen: true,
        onClick: () => {
          // Guarded: `clipboard` is undefined without a secure context, and an unguarded call
          // throws out of the handler and reads as the button doing nothing.
          void navigator.clipboard?.writeText(`Subject: ${e.subject}\n\n${e.body}`);
        },
      },
    ],
  });

  return (
    <div className="cdeck-briefing">
      <button type="button" className="cdeck-briefing-go" onClick={compose} disabled={busy}>
        {busy ? 'Composing…' : 'Compose a prospect briefing'}
      </button>
      <p className="cdeck-briefing-note">
        Written from what you have already published. You choose who receives it; MO OS keeps no
        list and sends nothing.
      </p>
      {why !== null && <p className="cdeck-briefing-why">{why}</p>}
      {edition !== null && <DeckDetail item={item(edition)} onClose={() => setEdition(null)} />}
    </div>
  );
}
